'use client'

/**
 * The pieces the banner, the popup and the `/whats-new` cards share
 * (#14619): the icon-or-image visual and the call-to-action link.
 */

import type { Route } from 'next'

import { ExternalLink, Megaphone } from '@/components/icons'
import { ButtonLink } from '@/components/ui/button-link'
import { announcementCtaTarget } from '@/lib/announcements/delivery'
import { getDashboardIcon } from '@/lib/dashboard-icons'
import { cn } from '@/lib/utils'

/** The author's icon, or a megaphone when none was picked. */
export function AnnouncementIcon({
  iconName,
  className,
}: {
  iconName: string | null
  className?: string
}) {
  const Icon = getDashboardIcon(iconName) ?? Megaphone
  return <Icon aria-hidden="true" className={cn('h-5 w-5', className)} />
}

/**
 * The image when the announcement has one (decorative — the title carries
 * the meaning), otherwise the icon.
 */
export function AnnouncementVisual({
  iconName,
  imageUrl,
  imageClassName,
}: {
  iconName: string | null
  imageUrl: string | null
  imageClassName?: string
}) {
  if (imageUrl) {
    return (
      // A plain <img>: the URL is a signed, short-lived storage URL that
      // next/image would proxy and cache past its expiry.
      <img
        src={imageUrl}
        alt=""
        className={cn('rounded-md object-cover', imageClassName)}
      />
    )
  }
  return (
    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-fill-10 text-text-link">
      <AnnouncementIcon iconName={iconName} />
    </span>
  )
}

/**
 * The call-to-action, as a link: an in-app path navigates in the same tab,
 * an `https:` URL opens a new one with `noopener noreferrer`. Nothing for a
 * missing label or a link outside those two forms.
 */
export function AnnouncementCta({
  label,
  url,
  onActivate,
  variant = 'default',
  className,
}: {
  label: string | null
  url: string | null
  onActivate: () => void
  variant?: 'default' | 'outline'
  className?: string
}) {
  const target = announcementCtaTarget(url)
  if (!label || !url || !target) return null
  const external = target === 'new-tab'
  return (
    <ButtonLink
      href={url as Route}
      variant={variant}
      size="sm"
      onClick={onActivate}
      className={cn('pointer-coarse:min-h-[44px] flex-shrink-0', className)}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {label}
      {external && (
        <>
          <ExternalLink aria-hidden="true" className="ml-1 h-4 w-4" />
          <span className="sr-only">(opens in a new tab)</span>
        </>
      )}
    </ButtonLink>
  )
}
